import { jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
import validators from "@lg-plus/loans-cz/custom-validators";
import defs from "@lg-plus/loans-cz/forms";
import { usePlasmicCanvasContext } from "@plasmicapp/react-web/lib/host";
import { useAwaitContext } from "../../shared/contexts/AwaitContext.js";
import { useFormRenderContext } from "./FormRenderContext.js";
import { useSubmissionContext } from "./SubmissionContext.js";
import { SurveyForm } from "./SurveyForm.js";
import { themeOpts } from "./SurveyForm.types.js";
export const LGFormProps = {
    theme: {
        type: "choice",
        options: themeOpts,
        defaultValue: "LayeredLight",
        displayName: "Theme Preset",
    },
    styles: {
        type: "object",
        displayName: "Custom Theme",
        description: "Object describing the custom theme to apply to the form. You can create it using Themes tab in SurveyJS Builder.",
    },
    css: {
        type: "code",
        displayName: "Custom CSS",
        description: "Custom CSS styles to apply to the form.",
        lang: "css",
    },
    customClasses: {
        type: "object",
        displayName: "Custom CSS Classes",
        description: "Object describing the custom CSS classes to apply to specific questions. The keys should be question names, and the values should be the CSS class names to apply.",
    },
    previewKey: {
        type: "choice",
        options: ["", "SHORT_REG", "FULL_REQUEST", "FULL_MULTIPAGE", "FULL_MULTIPAGE_SIMPLIFIED"],
        defaultValue: "",
        displayName: "Preview key",
        description: "Render the form for the given preset key (only for previewing in the Plasmic Studio).",
    },
    previewDefinition: {
        type: "object",
        displayName: "Preview Definition",
        description: "SurveyJS form definition to use (only for previewing in the Plasmic Studio).",
    },
    previewData: {
        type: "object",
        displayName: "Preview Data",
        description: "Data to prefill the form with (only for previewing in the Plasmic Studio).",
    },
    previewProgress: {
        type: "boolean",
        displayName: "Preview Show Progress",
        description: "If true, a progress indicator will be shown instead of the form (only for previewing in the Plasmic Studio).",
        defaultValue: false,
    },
    children: {
        type: "slot",
        displayName: "Children",
        description: "Optional children to render inside the container while component is in progress state.",
    },
};
const SMARTFORM_INSTANCE_CLASS_PREFIX = "smartform-instance";
/**
 * @todo Refactor to a shared utility (now duplicated in tests code)?
 */
const slugify = (text) => {
    return text
        .toLowerCase()
        .trim()
        .replace(/[\s-]+/g, "_") // Replace spaces and hyphens with _
        .replace(/[^\w-]+/g, "") // Remove all non-word chars
        .replace(/_{2,}/g, "_"); // Replace multiple _ with single _
};
const renderSurveyForm = (props) => {
    const definition = props.preset ? defs[props.preset] : props.definition || {};
    return (_jsx("div", { id: slugify(props.formKey), className: props.className, children: _jsx(SurveyForm, { definition: definition, ...props.commonProps }) }));
};
export function LGForm(props) {
    const { isAwaiting } = useAwaitContext();
    const { submit } = useSubmissionContext();
    const { form, data } = useFormRenderContext();
    const className = props.className;
    const inEditor = usePlasmicCanvasContext();
    if (isAwaiting || (inEditor && props.previewProgress)) {
        // return (<ProgressIndicator className={className} />);
        //TODO: progress indicator is not implemented yet, using a placeholder to make tests pass:
        return (_jsx(_Fragment, { children: _jsx("div", { id: "wait-message", className: className, children: props.children || "Please, wait..." }) }));
    }
    const formKey = form
        ? form?.formType === "preset"
            ? form.preset
            : form.key
        : props.previewKey || "";
    const commonProps = {
        theme: props.theme,
        styles: props.styles,
        css: props.css,
        customClasses: props.customClasses,
        data: data || props.previewData,
        validators,
        onSubmit: submit
            ? (data) => {
                //TODO: handle promise rejection:
                submit({
                    formKey,
                    data,
                });
            }
            : undefined,
        onPageRendered: (questionNames) => {
            const ownsSmartformField = questionNames.some((name) => props.customClasses?.[name]?.includes(SMARTFORM_INSTANCE_CLASS_PREFIX));
            if (!ownsSmartformField) {
                return;
            }
            if (window && window.smartform?.rebindAllForms) {
                window.smartform.rebindAllForms();
            }
        },
    };
    if (form) {
        switch (form.formType) {
            case "preset":
                return renderSurveyForm({
                    formKey,
                    preset: form.preset,
                    commonProps,
                    className,
                });
            case "custom":
                return renderSurveyForm({
                    formKey,
                    definition: form.schema,
                    commonProps,
                    className,
                });
            default:
                // normally should not enter here
                throw new Error(`Unsupported form type`);
        }
    }
    // preview mode
    return renderSurveyForm({
        formKey,
        preset: props.previewKey || undefined,
        definition: props.previewDefinition,
        commonProps,
        className,
    });
}
